<template>
  <div
    class="tw:flex tw:flex-col tw:items-center tw:text-center tw:pb-12"
    data-testid="active-orders-empty-view"
    :data-variant="hasActiveFilters ? 'filtered' : 'unfiltered'">
    <!-- ripple animation: droplet + expanding rings + 4 candidate nodes lighting up in sequence -->
    <div class="ripple" aria-hidden="true">
      <span class="ripple__pond" />
      <span class="ripple__ring ripple__ring--0" />
      <span class="ripple__ring ripple__ring--1" />
      <span class="ripple__ring ripple__ring--2" />
      <span
        v-for="(node, i) in nodes"
        :key="i"
        class="ripple__node"
        :class="`ripple__node--${i}`"
        :style="{ left: `${W / 2 + node.x - 9}px`, top: `${cy + node.y - 9}px` }">
        <IconUser :size="10" :stroke-width="2" />
      </span>
      <span class="ripple__drop" />
    </div>

    <h2 class="tw:mt-5 tw:mb-0 tw:text-20 tw:font-semibold tw:text-gray-900">{{ resolvedTitle }}</h2>
    <p class="tw:mt-2 tw:mb-0 tw:max-w-[440px] tw:text-14 tw:text-muted" style="text-wrap: pretty">
      {{ resolvedBody }}
    </p>

    <div class="tw:mt-6 tw:flex tw:items-center tw:gap-3">
      <LeadButton
        v-if="featureStore.featureCanCreateEditOrder"
        :as="RouterLink"
        to="/dashboard/orders/create"
        variant="primary"
        :icon="IconPlus"
        data-testid="active-orders-empty-view-create-order">
        {{ $t('orders.create.btn') }}
      </LeadButton>
      <LeadButton
        v-if="hasActiveFilters"
        variant="white"
        :icon="IconX"
        data-testid="active-orders-empty-view-reset-filters"
        @click="$emit('resetFilters')">
        {{ $t('orders.filters.reset_filters') }}
      </LeadButton>
      <LeadButton
        v-else
        variant="white"
        :trailing-icon="IconArrowRight"
        data-testid="active-orders-empty-view-go-to-all"
        @click="$emit('goToAll')">
        {{ $t('orders.list.empty.active.go_to_all') }}
      </LeadButton>
    </div>

    <!-- decorative ghost rows fading out towards the bottom (from the design).
         no max-width — the ghosts should fill the same container the real order rows would. -->
    <div class="ghosts tw:mt-11 tw:flex tw:flex-col tw:gap-2.5 tw:w-full" aria-hidden="true">
      <div
        v-for="(g, i) in ghosts"
        :key="i"
        class="tw:flex tw:items-center tw:gap-5 tw:rounded-md tw:border tw:border-solid tw:border-border tw:bg-surface tw:px-4 tw:py-4.5">
        <span class="tw:rounded-md tw:bg-gray-100 tw:shrink-0" style="width: 44px; height: 44px" />
        <div class="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:gap-2">
          <span class="tw:rounded-full tw:bg-gray-100" :style="{ height: '8px', width: g.w1 }" />
          <span class="tw:rounded-full tw:bg-gray-200" :style="{ height: '12px', width: g.w2 }" />
          <div class="tw:mt-1 tw:flex tw:gap-2">
            <span
              class="tw:rounded-full"
              :class="g.pill ? 'tw:bg-blue-50' : 'tw:bg-gray-100'"
              style="height: 20px; width: 64px" />
            <span class="tw:rounded-full tw:bg-gray-100" style="height: 20px; width: 88px" />
            <span class="tw:rounded-full tw:bg-gray-100" style="height: 20px; width: 56px" />
          </div>
        </div>
        <div
          class="tw:flex tw:items-end tw:gap-1.5 tw:border-l tw:border-solid tw:border-border-secondary tw:pl-5"
          style="height: 44px">
          <span
            v-for="(bar, bi) in g.bars"
            :key="bi"
            class="tw:rounded-xs"
            :class="g.pill && bi === g.bars.length - 1 ? 'tw:bg-blue-50' : 'tw:bg-gray-100'"
            :style="{ width: '8px', height: bar }" />
        </div>
        <div class="tw:flex tw:gap-8 tw:pr-3 tw:pl-5">
          <div class="tw:flex tw:flex-col tw:gap-2">
            <span class="tw:rounded-full tw:bg-gray-100" style="height: 6px; width: 56px" />
            <span class="tw:rounded-full tw:bg-gray-200" style="height: 12px; width: 72px" />
          </div>
          <div class="tw:flex tw:flex-col tw:gap-2">
            <span class="tw:rounded-full tw:bg-gray-100" style="height: 6px; width: 48px" />
            <span class="tw:rounded-full tw:bg-gray-200" style="height: 12px; width: 56px" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { IconPlus, IconArrowRight, IconUser, IconX } from '@tabler/icons-vue';
import { RouterLink } from 'vue-router';
import LeadButton from '@/components/lead-design/LeadButton.vue';
import { useCustomerFeaturesStore } from '@/stores/customerFeatures';
import translator from '@/locale/translator';

interface Props {
  hasActiveFilters?: boolean;
  title?: string;
  body?: string;
}
const props = withDefaults(defineProps<Props>(), { hasActiveFilters: false });

defineEmits<{ goToAll: []; resetFilters: [] }>();

const featureStore = useCustomerFeaturesStore();

// title/body fall back to defaults keyed off the filter state — filtered-empty and
// unfiltered-empty read differently, but callers can override either with a prop.
const resolvedTitle = computed(
  () =>
    props.title ??
    (translator.get(props.hasActiveFilters ? 'orders.list.empty' : 'orders.list.empty.active.title') as string)
);
const resolvedBody = computed(
  () =>
    props.body ??
    (translator.get(props.hasActiveFilters ? 'orders.list.empty.hint' : 'orders.list.empty.active.body') as string)
);

const W = 160;
const cy = 62;
const nodes = [
  { x: -30, y: 5 },
  { x: 46, y: -8 },
  { x: -64, y: -3 },
  { x: 70, y: 6 },
];

const ghosts = [
  { w1: '22%', w2: '48%', pill: true, days: [8, 12, 11, 16, 19, 22, 26] },
  { w1: '18%', w2: '38%', pill: false, days: [14, 18, 16, 20, 18, 22, 21] },
  { w1: '26%', w2: '54%', pill: false, days: [10, 9, 13, 12, 15, 14, 17] },
  { w1: '20%', w2: '42%', pill: false, days: [6, 8, 7, 10, 9, 11, 12] },
].map((g) => ({
  ...g,
  bars: g.days.map((v) => `${6 + v}px`),
}));
</script>

<style scoped>
/*
  Colors are hardcoded (not var(--color-primary)) because the LEAD tokens live inside
  Tailwind v4's `@theme { … }` block in tailwind.css — that registers them as Tailwind
  utility tokens but does NOT expose them as plain CSS custom properties on :root,
  so var() lookups from arbitrary CSS resolve to nothing.
*/
.ripple {
  position: relative;
  width: 160px;
  height: 88px;
  flex: none;
  margin-top: 20px;
  overflow: visible;
}
.ripple__pond {
  position: absolute;
  left: 14px;
  right: 14px;
  top: 52px;
  height: 20px;
  border-radius: 50%;
  background: rgba(0, 122, 255, 0.08);
}
.ripple__ring {
  position: absolute;
  left: 0;
  right: 0;
  top: 44px;
  height: 36px;
  border-radius: 50%;
  border: 2px solid var(--tw-color-primary);
  opacity: 0;
  animation: rp-ring 3s linear infinite;
  will-change: transform, opacity;
}
.ripple__ring--1 {
  animation-delay: 0.35s;
}
.ripple__ring--2 {
  animation-delay: 0.7s;
}
.ripple__drop {
  position: absolute;
  left: calc(50% - 6px);
  top: 50px;
  width: 12px;
  height: 14px;
  border-radius: 50% 50% 50% 50% / 60% 60% 40% 40%;
  background: var(--tw-color-primary);
  animation: rp-drop 3s cubic-bezier(0.55, 0, 0.85, 0.35) infinite;
}
.ripple__node {
  position: absolute;
  width: 18px;
  height: 18px;
  box-sizing: border-box;
  border-radius: 50%;
  border: 1px solid #eaecf0;
  background: #fff;
  color: #98a2b3;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.06);
}
.ripple__node--0 {
  animation: rp-lit0 3s linear infinite;
}
.ripple__node--1 {
  animation: rp-lit1 3s linear infinite;
}
.ripple__node--2 {
  animation: rp-lit2 3s linear infinite;
}
.ripple__node--3 {
  animation: rp-lit3 3s linear infinite;
}

@keyframes rp-drop {
  0% {
    transform: translateY(-38px);
    opacity: 0;
  }
  6% {
    opacity: 1;
  }
  19% {
    transform: translateY(0);
    opacity: 1;
  }
  22%,
  100% {
    transform: translateY(0) scale(0.4);
    opacity: 0;
  }
}
@keyframes rp-ring {
  0%,
  20% {
    transform: scale(0.08);
    opacity: 0;
  }
  22% {
    opacity: 0.9;
  }
  65% {
    transform: scale(1);
    opacity: 0.35;
  }
  75%,
  100% {
    transform: scale(1.08);
    opacity: 0;
  }
}
/* nodes light up in sequence as the ripple reaches them */
@keyframes rp-lit0 {
  0%,
  40% {
    background: #fff;
    color: #98a2b3;
    border-color: #eaecf0;
    transform: scale(1);
  }
  42% {
    background: var(--tw-color-primary);
    color: #fff;
    border-color: var(--tw-color-primary);
    transform: scale(1.3);
  }
  48%,
  88% {
    background: var(--tw-color-primary);
    color: #fff;
    border-color: var(--tw-color-primary);
    transform: scale(1);
  }
  95%,
  100% {
    background: #fff;
    color: #98a2b3;
    border-color: #eaecf0;
    transform: scale(1);
  }
}
@keyframes rp-lit1 {
  0%,
  52% {
    background: #fff;
    color: #98a2b3;
    border-color: #eaecf0;
    transform: scale(1);
  }
  54% {
    background: var(--tw-color-primary);
    color: #fff;
    border-color: var(--tw-color-primary);
    transform: scale(1.3);
  }
  60%,
  88% {
    background: var(--tw-color-primary);
    color: #fff;
    border-color: var(--tw-color-primary);
    transform: scale(1);
  }
  95%,
  100% {
    background: #fff;
    color: #98a2b3;
    border-color: #eaecf0;
    transform: scale(1);
  }
}
@keyframes rp-lit2 {
  0%,
  56% {
    background: #fff;
    color: #98a2b3;
    border-color: #eaecf0;
    transform: scale(1);
  }
  58% {
    background: var(--tw-color-primary);
    color: #fff;
    border-color: var(--tw-color-primary);
    transform: scale(1.3);
  }
  64%,
  88% {
    background: var(--tw-color-primary);
    color: #fff;
    border-color: var(--tw-color-primary);
    transform: scale(1);
  }
  95%,
  100% {
    background: #fff;
    color: #98a2b3;
    border-color: #eaecf0;
    transform: scale(1);
  }
}
@keyframes rp-lit3 {
  0%,
  61% {
    background: #fff;
    color: #98a2b3;
    border-color: #eaecf0;
    transform: scale(1);
  }
  63% {
    background: var(--tw-color-primary);
    color: #fff;
    border-color: var(--tw-color-primary);
    transform: scale(1.3);
  }
  69%,
  88% {
    background: var(--tw-color-primary);
    color: #fff;
    border-color: var(--tw-color-primary);
    transform: scale(1);
  }
  95%,
  100% {
    background: #fff;
    color: #98a2b3;
    border-color: #eaecf0;
    transform: scale(1);
  }
}

.ghosts {
  -webkit-mask-image: linear-gradient(to bottom, #000 0%, rgba(0, 0, 0, 0.35) 55%, transparent 100%);
  mask-image: linear-gradient(to bottom, #000 0%, rgba(0, 0, 0, 0.35) 55%, transparent 100%);
}

@media (prefers-reduced-motion: reduce) {
  .ripple__ring,
  .ripple__drop,
  .ripple__node {
    animation: none !important;
  }
}
</style>
